import { ConfigFormHeader, useConfigViewOnly } from '@/components/ConfigFormHeader'
import { ComplianceRiskConfig } from '@/types/configuration'
import { Badge, Button, Card, CloseButton, Group, Stack, Text, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import { IconPlus } from '@tabler/icons-react'
import { useEffect, useState } from 'react'

interface ComplianceRiskFormProps {
  data: ComplianceRiskConfig | null
  onSave: (data: ComplianceRiskConfig) => void
  isSaving: boolean
}

export function ComplianceRiskForm({ data, onSave, isSaving }: ComplianceRiskFormProps) {
  const viewOnly = useConfigViewOnly()
  const form = useForm<ComplianceRiskConfig>({
    initialValues: {
      optOutKeywords: [],
      keywordBlacklist: [],
    },
  })

  const [optOutKeyword, setOptOutKeyword] = useState('')
  const [blacklistKeyword, setBlacklistKeyword] = useState('')

  useEffect(() => {
    if (data) {
      form.setValues(data)
    }
  }, [data])

  const addKeyword = (field: 'optOutKeywords' | 'keywordBlacklist', input: string) => {
    if (!input.trim()) return

    // Split by comma and process each keyword
    const newKeywords = input
      .split(',')
      .map(k => k.trim())
      .filter(k => k.length > 0)

    if (newKeywords.length === 0) return

    // Get existing keywords and add new ones (avoiding duplicates)
    const existingKeywords = form.values[field]
    const uniqueNewKeywords = newKeywords.filter(k => !existingKeywords.includes(k))

    if (uniqueNewKeywords.length > 0) {
      form.setFieldValue(field, [...existingKeywords, ...uniqueNewKeywords])
    }

    // Clear input field
    if (field === 'optOutKeywords') setOptOutKeyword('')
    else setBlacklistKeyword('')
  }

  const removeKeyword = (field: 'optOutKeywords' | 'keywordBlacklist', keyword: string) => {
    const keywords = form.values[field].filter(k => k !== keyword)
    form.setFieldValue(field, keywords)
  }

  return (
    <form onSubmit={form.onSubmit(onSave)}>
      <Stack gap="md">
        <ConfigFormHeader onReset={() => form.reset()} isSaving={isSaving} />
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <div>
              <Text fw={600}>SMS Opt-out Keywords</Text>
              <Text size="xs" c="dimmed">
                Keywords that trigger automatic opt-out for TCPA compliance (e.g., STOP, CANCEL, UNSUBSCRIBE)
              </Text>
            </div>
            <Group wrap="wrap" gap="xs">
              {form.values.optOutKeywords.map(keyword => (
                <Badge
                  key={keyword}
                  color="red"
                  pr={3}
                  rightSection={<CloseButton size="xs" onClick={() => removeKeyword('optOutKeywords', keyword)} />}
                >
                  {keyword}
                </Badge>
              ))}
            </Group>
            <Group>
              <TextInput
                placeholder="e.g., STOP, CANCEL, UNSUBSCRIBE, QUIT (comma-separated)"
                value={optOutKeyword}
                onChange={e => setOptOutKeyword(e.currentTarget.value)}
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addKeyword('optOutKeywords', optOutKeyword))}
                className="flex-1"
              />
              <Button
                disabled={viewOnly}
                size="xs"
                variant="filled"
                leftSection={<IconPlus size={14} />}
                onClick={() => addKeyword('optOutKeywords', optOutKeyword)}
              >
                Add
              </Button>
            </Group>
          </Stack>
        </Card>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <div>
              <Text fw={600}>Spam Keywords</Text>
              <Text size="xs" c="dimmed">
                Messages containing these keywords will be ignored as spam (e.g., random promotional texts)
              </Text>
            </div>
            <Group wrap="wrap" gap="xs">
              {form.values.keywordBlacklist.map(keyword => (
                <Badge
                  key={keyword}
                  color="orange"
                  pr={3}
                  rightSection={<CloseButton size="xs" onClick={() => removeKeyword('keywordBlacklist', keyword)} />}
                >
                  {keyword}
                </Badge>
              ))}
            </Group>
            <Group>
              <TextInput
                placeholder="e.g., WIN, PRIZE, FREE, CLICK, URGENT, CONGRATULATIONS (comma-separated)"
                value={blacklistKeyword}
                onChange={e => setBlacklistKeyword(e.currentTarget.value)}
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addKeyword('keywordBlacklist', blacklistKeyword))}
                className="flex-1"
              />
              <Button
                disabled={viewOnly}
                size="xs"
                variant="filled"
                leftSection={<IconPlus size={14} />}
                onClick={() => addKeyword('keywordBlacklist', blacklistKeyword)}
              >
                Add
              </Button>
            </Group>
          </Stack>
        </Card>
      </Stack>
    </form>
  )
}
